/**
 * Persisted run → the shape the pure evaluator reads.
 *
 * Client-safe by construction: it only touches `./types`, never
 * `@repo/database`. That matters because the same mapping runs on both sides —
 * the server calls it in `run-service.ts` before evaluating, and a client
 * component can call it on a run that arrived over JSON.
 *
 * The two JSON columns are `unknown` as far as Prisma is concerned, so this is
 * also the one place that narrows them. It is deliberately forgiving: a run
 * written by an older build, or hand-edited in Studio, degrades to "no
 * overrides" rather than throwing inside an evaluation.
 */

import type {
	WorkflowRunContext,
	WorkflowRunLike,
	WorkflowStepState,
} from "./types";

/**
 * Structural stand-in for a `WorkflowRun` row. Spelled out rather than imported
 * from `@repo/database` so this module stays reachable from `@repo/core/client`.
 */
export interface WorkflowRunRecordLike {
	id: string;
	propertyId: string | null;
	startedAt: Date | string;
	startedByUserId: string | null;
	stepStates: unknown;
	context: unknown;
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readString(
	source: Record<string, unknown>,
	key: string,
): string | undefined {
	const value = source[key];
	return typeof value === "string" ? value : undefined;
}

/** Copies only the fields the evaluator knows about; drops everything else. */
function readStepStates(value: unknown): Record<string, WorkflowStepState> {
	if (!isRecord(value)) {
		return {};
	}
	const out: Record<string, WorkflowStepState> = {};
	for (const [stepId, raw] of Object.entries(value)) {
		if (!isRecord(raw)) {
			continue;
		}
		const state: WorkflowStepState = {};
		const skippedAt = readString(raw, "skippedAt");
		const markedDoneAt = readString(raw, "markedDoneAt");
		const markedDoneByUserId = readString(raw, "markedDoneByUserId");
		if (skippedAt) {
			state.skippedAt = skippedAt;
		}
		if (markedDoneAt) {
			state.markedDoneAt = markedDoneAt;
		}
		if (markedDoneByUserId) {
			state.markedDoneByUserId = markedDoneByUserId;
		}
		if (Object.keys(state).length > 0) {
			out[stepId] = state;
		}
	}
	return out;
}

const CONTEXT_KEYS = [
	"tag",
	"period",
	"locationPropertyId",
	"coverageGroupId",
	"pipelineRunId",
	"targetKey",
	"targetLabel",
] as const satisfies ReadonlyArray<keyof WorkflowRunContext>;

export function readWorkflowRunContext(value: unknown): WorkflowRunContext {
	if (!isRecord(value)) {
		return {};
	}
	const out: WorkflowRunContext = {};
	for (const key of CONTEXT_KEYS) {
		const read = readString(value, key);
		if (read) {
			out[key] = read;
		}
	}
	return out;
}

export function toWorkflowRunLike(run: WorkflowRunRecordLike): WorkflowRunLike {
	return {
		id: run.id,
		propertyId: run.propertyId,
		startedAt:
			run.startedAt instanceof Date
				? run.startedAt.toISOString()
				: run.startedAt,
		startedByUserId: run.startedByUserId,
		stepStates: readStepStates(run.stepStates),
		context: readWorkflowRunContext(run.context),
	};
}
